/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useReducer, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useIntl, defineMessages } from 'react-intl';
import { Form, Button, TextArea, Loader, Message } from 'semantic-ui-react';
import { isCmsUi } from '@plone/volto/helpers';
import {
  submitCustomerSatisfaction,
  resetSubmitCustomerSatisfaction,
} from 'volto-customer-satisfaction/actions';

// Import exactly like the view you shared
import { Captcha, HoneypotWidget } from 'volto-form-block/components/Widget';

import 'volto-customer-satisfaction/components/CustomerSatisfaction/customer-satisfaction.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown } from '@fortawesome/free-regular-svg-icons';
import { ReportErrorForm } from '../';
import { getRegistry } from '../../actions';


const messages = defineMessages({
  title: { id: 'customer_satisfaction_title', defaultMessage: 'Was this page useful to you?' },
  yes: { id: 'customer_satisfaction_yes', defaultMessage: 'Yes, very useful' },
  no: { id: 'customer_satisfaction_no', defaultMessage: 'Not much' },
  suggestions_placeholder: { id: 'customer_satisfaction_suggestions_placeholder', defaultMessage: 'Explain us why, and help us improve the quality of the site' },
  submit: { id: 'customer_satisfaction_submit', defaultMessage: 'Submit your comment' },
  thank_you: { id: 'customer_satisfaction_thank_you', defaultMessage: 'Gràcies pels teus comentaris!' },
  comment_label: { id: 'customer_satisfaction_comment', defaultMessage: 'Your comment' },
});

const CustomerSatisfaction = () => {
  const intl = useIntl();
  const location = useLocation();
  const path = location.pathname ?? '/';
  const dispatch = useDispatch();

  const [satisfaction, setSatisfaction] = useState(null);
  const [isErrorFormVisible, setIsErrorFormVisible] = useState(false);
  const [formData, setFormData] = useState({});
  
  const submitResults = useSelector((state) => state.submitCustomerSatisfaction);
  
  const registryRecord = 'plone.formwidget.recaptcha.interfaces.IReCaptchaSettings.public_key'
  const siteKey = useSelector((state) => state?.registry?.records[registryRecord]);
  const captchaToken = useRef();

  const captcha = new Captcha({
    captchaToken,
    captcha: 'recaptcha',
    captcha_props: {
      public_key: siteKey,
    },
    onChangeFormData: (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    },
  });

  const changeSatisfaction = (e, s) => {
    e.stopPropagation();
    e.preventDefault();
    setIsErrorFormVisible(false);
    setSatisfaction(s === satisfaction ? null : s);
  };

  useEffect(() => {
    dispatch(getRegistry(registryRecord));
  }, [dispatch]);

  useEffect(() => {
    setSatisfaction(null);
    setIsErrorFormVisible(false);
    return () => { dispatch(resetSubmitCustomerSatisfaction()); };
  }, [path]);

  const sendFormData = (e) => {
    e.preventDefault();
    captcha.verify().then(() => {
      dispatch(
        submitCustomerSatisfaction(path, {
          ...formData,
          vote: satisfaction === true ? 'ok' : 'nok',
          'g-recaptcha-response': captchaToken.current,
        }),
      );
    });
  };

  if (isCmsUi(path)) return null;

  return (
    <aside className="customer-satisfaction d-print-none">
      <h2 className="customer-satisfaction__title">{intl.formatMessage(messages.title)}</h2>

      <div className="customer-satisfaction-wrapper-buttons">
        <div className="buttons">
          {!submitResults?.loaded && (
            <>
              <Button onClick={(e) => changeSatisfaction(e, true)} active={satisfaction === true} className="customer-satisfaction__btn customer-satisfaction__btn--yes">
                {intl.formatMessage(messages.yes)}
                <FontAwesomeIcon icon={faThumbsUp} className="ms-3 fs-5" />
              </Button>
              <Button onClick={(e) => changeSatisfaction(e, false)} active={satisfaction === false} className="customer-satisfaction__btn customer-satisfaction__btn--no">
                {intl.formatMessage(messages.no)}
                <FontAwesomeIcon icon={faThumbsDown} className="ms-3 fs-5" />
              </Button>
            </>
          )}

          <ReportErrorForm
            isFormVisible={isErrorFormVisible}
            onOpen={() => {
              setSatisfaction(null);
              setIsErrorFormVisible(!isErrorFormVisible);
            }}
            onlyButton
          />
        </div>

        {!submitResults?.loading && !submitResults.loaded && satisfaction !== null && (
          <Form onSubmit={sendFormData} className="mt-4">
            <div className="comment">
              <label htmlFor="cs-comment-textarea">{intl.formatMessage(messages.comment_label)}</label>
              <TextArea
                id="cs-comment-textarea"
                placeholder={intl.formatMessage(messages.suggestions_placeholder)}
                onChange={(e, v) => setFormData({ ...formData, comment: v.value })}
              />
            </div>

            {siteKey && (
                captcha.render()
            )}

            <div className="submit-wrapper">
              <Button type="submit" content={intl.formatMessage(messages.submit)} primary loading={submitResults?.loading} />
            </div>
          </Form>
        )}

        {submitResults?.loading && <Loader active inline="centered" className="mt-4" />}
        {submitResults?.loaded && (
          <Message positive className="mt-4">
            <p>{intl.formatMessage(messages.thank_you)}</p>
          </Message>
        )}

        <ReportErrorForm isFormVisible={isErrorFormVisible} onlyForm />
      </div>
    </aside>
  );
};

export default CustomerSatisfaction;